"use client";

import { createContext, useContext } from "react";
import type { SortOption } from "@/components/search/SortDialog";
import type { Locale } from "@/i18n/routing";
import type { BikeClass } from "@/lib/mobility/bike-classes";
import type {
  HubAxes,
  PresentFacets,
  ProductFacets,
} from "@/lib/mobility/bike-facets";
import type { Product } from "@/types";
import type { StationHubData, StationSummary } from "@/hooks/useStationHubData";
import type { PlacePoint } from "@/lib/landmarks/client";
import type { HubKind } from "@/lib/mobility/station-hub";
import type { HubModelLink } from "@/lib/api/model-hub";
export { hubKey } from "@/lib/mobility/station-hub";

/** 시트(모바일) · 좌측 패널(데스크톱)에 무엇이 들어가는지 — spec §3-1. */
export type HubSheet =
  | { kind: "nearby" }
  /** 지점을 누르면 카드 단계 없이 곧바로 그 지점의 차종 목록이 열린다. */
  | { kind: "bikes"; stationId: string }
  | { kind: "catalog" }
  | { kind: "model"; modelKey: string };

export type HubTranslate = (
  key: string,
  params?: Record<string, string | number>
) => string;

export interface HubContextValue {
  /** 허브 종류 — 캠핑이면 문구가 캠핑장·장비로 바뀌고 바이크 전용 요소(클래스·스펙)를 숨긴다. */
  kind: HubKind;
  locale: string;
  /** `useTranslations("search")` — 허브 키는 전부 `hub.*`. */
  t: HubTranslate;
  data: StationHubData;
  /** 거리 기준점(현재 위치 > 지도 중심). */
  origin: { lat: number; lng: number } | null;
  /** 거리 문구에 쓰는 기준점 이름(검색 지역명). */
  originLabel: string;
  /**
   * "내 주변"을 기본 탭으로 열어도 되는가 — 위치 권한이 이미 있고 지도 중심에서
   * 200km 이내이거나, 사용자가 **명시적으로** 내 주변/현 위치에서 검색을 눌렀을 때만 true.
   * 처음부터 bounds 가 박힌 URL 은 사용자의 의도가 아니므로 포함하지 않는다.
   */
  defaultNearby: boolean;
  /** 거리 기준점이 사용자 위치가 아니라 지도 중심인가 — 정렬 근거를 문구로 알린다. */
  originIsMapCenter: boolean;
  /**
   * 명소 필터에서 고른 명소(09-23). 있으면 거리 기준점이 이 명소이고, 지점 목록은
   * 가까운 순 + 행마다 "명소에서 N km". 없으면 null.
   */
  landmark: PlacePoint | null;
  /** 명소 이름(현재 언어) — landmark 가 없으면 빈 문자열. */
  landmarkLabel: string;
  /** 현재 상품들에 실제로 존재하는 클래스만, BIKE_CLASSES 순서로. */
  classes: BikeClass[];
  /** 데이터셋에 실제로 있는 타입·라이딩·변속 값 — 알약 노출·옵션의 근거. */
  presentFacets: PresentFacets;
  /** 필터 축(배기량·타입·라이딩·변속) — URL 이 정본, 바꾸면 즉시 적용. */
  axes: HubAxes;
  setAxes: (axes: HubAxes) => void;
  /** 검색 페이지 정렬(가격 낮은순·높은순·최신순). null = 기본(배기량 섹션). */
  sortBy: SortOption | null;
  /** 검색 페이지의 정렬 다이얼로그를 연다. 없으면 정렬 버튼을 숨긴다. */
  onSortOpen?: () => void;
  /** 상품 id → 필터 축 값(클래스·제조사·타입·변속·라이딩). 없으면 undefined. */
  facetsOf: (productId: string) => ProductFacets | undefined;
  /**
   * 지점의 상품 중 현재 축을 모두 만족하는 것만. 축이 비어 있으면 원본 배열
   * 그대로(참조 동일 — memo 가 깨지지 않는다).
   */
  visibleProducts: (station: StationSummary) => Product[];
  open: (sheet: HubSheet) => void;
  /** 상품 상세 deep link — `?branch=` + 기존 checkin/checkout 전달. */
  productHref: (productId: string, stationId?: string) => string;
  /**
   * 지점 페이지 경로(`/[locale]/stations/{id8}-{slug}`) — 페이지가 없는 지점이면 undefined.
   * 서버가 내려준 맵이라 첫 렌더(SSR)부터 값이 있다.
   */
  stationHref: (stationId: string) => string | undefined;
  /**
   * 차종의 모델 허브 경로(`/[locale]/models/{slug}`)·이름 — 모델 키(요금제 접미사를 뗀 이름)로
   * 찾는다. 허브가 없는 모델이면 undefined(대표 상품 링크 유지, 시트의 "전체 보기" 없음).
   * 서버가 내려준 맵이다.
   */
  modelLink?: (modelKey: string) => HubModelLink | undefined;
  /**
   * 표시용 거리 — 기준점이 너무 멀면(다른 나라) null 을 돌려 화면에서 뺀다.
   * "1162km" 같은 숫자는 정보가 아니라 잡음이다.
   */
  distanceKm: (lat: number, lng: number) => number | null;
  /** 정렬 전용 실제 거리 — 표시 임계값과 무관하게 항상 값이 있다. */
  rawDistanceKm: (lat: number, lng: number) => number;
  /** 검색 날짜("YYYY-MM-DD") — 캠핑장 시트가 기간 요금·날짜 줄에 쓴다. 없으면 null. */
  checkinDate: string | null;
  checkoutDate: string | null;
  /** 검색 날짜 변경(검색 페이지와 같은 핸들러). 없으면 "날짜 변경" 버튼을 숨긴다. */
  onDateChange?: (checkin: string | null, checkout: string | null) => void;
  /** 지점 기준 통화 표시 문자열(환산 포함, 통화 코드 표기 "KRW 13,800"). */
  formatPrice: (amount: number, currency?: string) => string;
  /**
   * "이 지역에서 검색" 범위(MAP_AREA)로 지점을 좁혀 보고 있는가(09-29) — 빈 목록의
   * 원인 판정(`hubEmptyCause`)에 쓴다.
   */
  areaBounded: boolean;
  /** 지도 영역 범위를 주소에서 지워 전 지점으로 되돌린다(빈 화면의 "전체 지점 보기"). */
  clearArea: () => void;
  /**
   * 목록 카드의 "지도" 버튼(09-29) — 드로어를 내리고 지도를 그 지점으로 옮겨 핀을
   * 강조한다(시트는 열지 않는다). 지도가 늘 옆에 보이는 데스크톱에서는 undefined —
   * 버튼을 그리지 않는다.
   */
  focusStation?: (stationId: string) => void;
  /**
   * 데스크톱 목록 카드 호버 → 지도 핀 강조(09-29). 지도가 옆에 없는 모바일·태블릿은
   * undefined(터치의 가짜 mouseenter 로 핀이 켜진 채 남지 않게).
   */
  hoverStation?: (stationId: string | null) => void;
}

const HubContext = createContext<HubContextValue | null>(null);

export const HubProvider = HubContext.Provider;

/**
 * 행 밀도 — 같은 시트를 모바일 드로어(compact)와 데스크톱 목록 열(wide)이
 * 함께 쓰므로, 트리마다 값을 다르게 씌운다(뷰포트 추측·CSS 중복 마크업 없이).
 */
const HubDensityContext = createContext<"compact" | "wide">("compact");

export const HubDensityProvider = HubDensityContext.Provider;

export function useHubDensity(): "compact" | "wide" {
  return useContext(HubDensityContext);
}

export function useHub(): HubContextValue {
  const value = useContext(HubContext);
  if (!value) throw new Error("useHub must be used inside <StationHub>");
  return value;
}

/** 5개 언어 라벨 맵에서 현재 언어를 고른다 (없으면 ko → 첫 값). */
export function pickLabel(
  labels: Record<Locale, string> | Record<string, string> | undefined,
  locale: string
): string {
  if (!labels) return "";
  const map = labels as Record<string, string>;
  return map[locale] ?? map.ko ?? Object.values(map)[0] ?? "";
}

/** 1km 미만은 m, 그 이상은 소수 한 자리 km. 언어 무관 표기. */
export function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 100) * 10}m`;
  if (km < 10) return `${km.toFixed(1)}km`;
  return `${Math.round(km)}km`;
}

/** 부제 조각들을 " · " 로 잇는다(빈 값 제거). */
export function joinMeta(...parts: Array<string | null | undefined>): string {
  return parts.filter((p): p is string => !!p && p.length > 0).join(" · ");
}
